import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Archive, ArchiveRestore, Ellipsis, MapPin, Pencil, Plus } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { branchesQuery, membersQuery, useCreateBranch, useUpdateBranch } from '@/api/queries'
import type { Branch, Member } from '@/api/types'
import { BranchCover } from '@/app/branch-cover'
import { BranchHoursFields } from '@/app/branch-hours'
import { BranchLocationFields } from '@/app/branch-location'
import { useCompanyCaller } from '@/app/company'
import { NothingForRole } from '@/app/nothing-for-role'
import { TimezonePicker } from '@/app/timezone-picker'
import { canManageBranches, companySections } from '@/domain/abilities'
import {
  affectedByArchive,
  branchPlace,
  draftFromBranch,
  draftToCreate,
  draftToUpdate,
  isArchived,
  locationDraftOf,
  locationOf,
  locationProblems,
  locationToUpdate,
  splitBranches,
  type BranchDraft,
  type LocationDraft,
} from '@/domain/branches'
import { errorText } from '@/domain/errors'
import { hoursDraftOf, hoursProblems, hoursToUpdate, type HoursDraft } from '@/domain/opening-hours'
import { atLeast } from '@/domain/permissions'
import { canonicalZone, DEFAULT_ZONE, zoneCity, zoneOffset } from '@/domain/timezones'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { DataList, type DataColumn } from '@/ui/data-list'
import { Field } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem, SidePanel } from '@/ui/overlays'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/branches')({
  component: Branches,
})

type News = Readonly<{ kind: 'created' | 'archived' | 'restored'; name: string }>

/// Клуби: the company's physical locations. Seen from `settings ≥ view`;
/// opened, changed, archived and restored by `settings = manage` and gods —
/// the buttons exist only for them. A club is never deleted: other services
/// keep its id, so it goes to «Архів» and can come back.
function Branches() {
  const { orgId } = Route.useParams()
  const { caller } = useCompanyCaller(orgId)
  const allowed = companySections(caller).includes('branches')
  const manage = canManageBranches(caller)
  const branches = useQuery({ ...branchesQuery(orgId), enabled: allowed })
  // Names of the people an archive would leave without a club. Only asked
  // for by those who may see staff; everyone else gets the bare number.
  const members = useQuery({
    ...membersQuery(orgId),
    enabled: allowed && manage && atLeast(caller.profile.permissions.staff, 'view'),
  })
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<Branch | null>(null)
  const [archiving, setArchiving] = useState<Branch | null>(null)
  const [restoring, setRestoring] = useState<Branch | null>(null)
  const [news, setNews] = useState<News | null>(null)

  if (!allowed) return <NothingForRole />

  const { active, archived } = splitBranches(branches.data ?? [])
  const addButton = manage ? (
    <Button
      variant="primary"
      icon={<Plus className="size-[18px]" strokeWidth={2.5} />}
      onClick={() => {
        setNews(null)
        setAdding(true)
      }}
    >
      {t.branches.add}
    </Button>
  ) : undefined

  return (
    <Page wide>
      <PageHeader
        title={t.branches.title}
        meta={branches.data ? String(active.length) : undefined}
        subtitle={t.branches.subtitle}
        actions={branches.data && branches.data.length > 0 ? addButton : undefined}
      />

      {news ? (
        <InlineBanner
          kind="done"
          className="mb-6"
          message={
            news.kind === 'created'
              ? t.branches.created(news.name)
              : news.kind === 'archived'
                ? t.branches.archivedBanner(news.name)
                : t.branches.restoredBanner(news.name)
          }
        />
      ) : null}

      {branches.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : branches.isError ? (
        <InlineBanner
          kind="error"
          message={errorText(branches.error)}
          action={{ label: t.app.retry, onClick: () => void branches.refetch() }}
        />
      ) : branches.data.length === 0 ? (
        <EmptyState
          icon={<MapPin />}
          title={t.branches.empty}
          message={
            <>
              {t.branches.emptyMessage}
              {manage ? null : <span className="mt-2 block text-muted">{t.branches.emptyNotYours}</span>}
            </>
          }
          action={addButton}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {manage ? null : <InlineBanner kind="info" message={t.branches.readOnly} />}
          {active.length === 0 ? (
            <InlineBanner kind="empty" title={t.branches.allArchived} message={t.branches.allArchivedMessage} />
          ) : null}
          <DataList
            label={t.branches.title}
            columns={branchColumns}
            // Open clubs first; the archive stays in the same table, greyed —
            // a status, not a different kind of thing.
            rows={[...active, ...archived]}
            rowKey={(branch) => branch.id}
            rowTone={(branch) => (isArchived(branch) ? 'muted' : 'normal')}
            rowTarget={manage ? (branch) => ({ onClick: () => setEditing(branch) }) : undefined}
            rowLabel={(branch) => `${t.branches.edit}: ${branch.name}`}
            rowActions={
              manage
                ? (branch) => (
                    <BranchMenu
                      branch={branch}
                      onEdit={() => setEditing(branch)}
                      onArchive={() => setArchiving(branch)}
                      onRestore={() => setRestoring(branch)}
                    />
                  )
                : undefined
            }
          />
          {archived.length > 0 ? <p className="px-4 text-caption text-muted">{t.branches.archiveFootnote}</p> : null}
        </div>
      )}

      {manage ? (
        <>
          <AddBranchPanel
            open={adding}
            onOpenChange={setAdding}
            orgId={orgId}
            onCreated={(branch) => {
              setAdding(false)
              setNews({ kind: 'created', name: branch.name })
            }}
          />
          {editing ? (
            <EditBranchPanel
              key={editing.id}
              branch={editing}
              orgId={orgId}
              onClose={() => setEditing(null)}
              onArchive={() => setArchiving(editing)}
              onRestore={() => setRestoring(editing)}
            />
          ) : null}
          <ArchiveDialog
            branch={archiving}
            orgId={orgId}
            members={members.data ?? null}
            branches={branches.data ?? []}
            onClose={() => setArchiving(null)}
            onDone={(branch) => {
              setArchiving(null)
              setEditing(null)
              setNews({ kind: 'archived', name: branch.name })
            }}
          />
          <RestoreDialog
            branch={restoring}
            orgId={orgId}
            onClose={() => setRestoring(null)}
            onDone={(branch) => {
              setRestoring(null)
              setEditing(null)
              setNews({ kind: 'restored', name: branch.name })
            }}
          />
        </>
      ) : null}
    </Page>
  )
}

/// «Київ · UTC+3» — the zone's city and today's offset (daylight saving
/// moves it), the two things a person checks a zone by.
const zoneLine = (branch: Branch): string => {
  const zone = canonicalZone(branch.timezone)
  return [zoneCity(zone), zoneOffset(zone, new Date())].filter(Boolean).join(' · ')
}

/// Name, where, zone, open or archived, how many people are tied to it —
/// columns instead of a pin icon and a chevron. On a phone: the name with
/// the place under it, the state on the right.
const branchColumns: readonly DataColumn<Branch>[] = [
  { key: 'name', header: t.branches.columns.name, width: 'minmax(0,1.2fr)', cell: (branch) => branch.name },
  {
    key: 'place',
    header: t.branches.columns.place,
    width: 'minmax(0,1.6fr)',
    // A pin after the address: the club is on «Зали поруч» in the app.
    cell: (branch) => (
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate">{branchPlace(branch) || <span className="text-muted">—</span>}</span>
        {locationOf(branch) ? (
          <MapPin role="img" aria-label={t.sale.location.on} className="size-3.5 shrink-0 text-success" strokeWidth={2.25} />
        ) : null}
      </span>
    ),
  },
  {
    key: 'zone',
    header: t.branches.columns.zone,
    width: 'minmax(0,1fr)',
    hideBelow: 'md',
    mobile: 'hidden',
    cell: zoneLine,
  },
  {
    key: 'status',
    header: t.branches.columns.status,
    width: '104px',
    mobile: 'trailing',
    cell: (branch) =>
      isArchived(branch) ? (
        <span className="text-muted">{t.branches.archived}</span>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-text-2">
          <span aria-hidden="true" className="size-1.5 rounded-full bg-success" />
          {t.branches.open}
        </span>
      ),
  },
  {
    key: 'people',
    header: t.branches.columns.people,
    width: '72px',
    numeric: true,
    hideBelow: 'lg',
    mobile: 'hidden',
    cell: (branch) => (branch.scopedMembers ? String(branch.scopedMembers) : <span className="text-muted">—</span>),
  },
]

const BranchMenu = ({
  branch,
  onEdit,
  onArchive,
  onRestore,
}: {
  branch: Branch
  onEdit: () => void
  onArchive: () => void
  onRestore: () => void
}) => (
  <RowMenu label={`${t.app.more}: ${branch.name}`} trigger={<Ellipsis className="size-[18px]" />}>
    <RowMenuItem icon={<Pencil className="size-4" />} onSelect={onEdit}>
      {t.branches.edit}
    </RowMenuItem>
    {isArchived(branch) ? (
      <RowMenuItem icon={<ArchiveRestore className="size-4" />} onSelect={onRestore}>
        {t.branches.restoreAction}
      </RowMenuItem>
    ) : (
      <RowMenuItem icon={<Archive className="size-4" />} onSelect={onArchive} destructive>
        {t.branches.archiveAction}
      </RowMenuItem>
    )}
  </RowMenu>
)

/// Name, place and zone — the same fields in «Новий клуб» and the edit panel.
const BranchFields = ({
  draft,
  onChange,
  nameError,
  autoFocus = false,
}: {
  draft: BranchDraft
  onChange: (next: BranchDraft) => void
  nameError?: string | undefined
  autoFocus?: boolean
}) => (
  <>
    <Field
      autoFocus={autoFocus}
      label={t.branches.form.name}
      placeholder={t.branches.form.namePlaceholder}
      value={draft.name}
      error={nameError}
      onChange={(e) => onChange({ ...draft, name: e.target.value })}
    />
    <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3">
      <Field
        label={t.branches.form.city}
        placeholder={t.branches.form.cityPlaceholder}
        value={draft.city}
        onChange={(e) => onChange({ ...draft, city: e.target.value })}
      />
      <Field
        label={t.branches.form.address}
        placeholder={t.branches.form.addressPlaceholder}
        value={draft.address}
        onChange={(e) => onChange({ ...draft, address: e.target.value })}
      />
    </div>
    <TimezonePicker value={draft.timezone} onChange={(timezone) => onChange({ ...draft, timezone })} />
  </>
)

function AddBranchPanel({
  open,
  onOpenChange,
  orgId,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
  onCreated: (branch: Branch) => void
}) {
  const create = useCreateBranch(orgId)
  const [draft, setDraft] = useState<BranchDraft>(draftFromBranch(null, DEFAULT_ZONE))
  const [nameError, setNameError] = useState<string | undefined>(undefined)

  const reset = () => {
    setDraft(draftFromBranch(null, DEFAULT_ZONE))
    setNameError(undefined)
    create.reset()
  }
  const close = () => {
    onOpenChange(false)
    reset()
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!draft.name.trim()) {
      setNameError(t.branches.form.nameRequired)
      return
    }
    create.mutate(draftToCreate(draft), {
      onSuccess: (branch) => {
        reset()
        onCreated(branch)
      },
    })
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={t.branches.form.addTitle}
      onSubmit={submit}
      footer={
        <>
          <Button variant="quiet" onClick={close}>
            {t.app.cancel}
          </Button>
          <Button type="submit" variant="primary" busy={create.isPending}>
            {create.isPending ? t.branches.form.submitAdding : t.branches.form.submitAdd}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-7">
        <BranchFields
          autoFocus
          draft={draft}
          nameError={nameError}
          onChange={(next) => {
            setDraft(next)
            if (next.name.trim()) setNameError(undefined)
          }}
        />
        {create.isError ? <InlineBanner kind="error" message={errorText(create.error)} /> : null}
      </div>
    </SidePanel>
  )
}

function EditBranchPanel({
  branch,
  orgId,
  onClose,
  onArchive,
  onRestore,
}: {
  branch: Branch
  orgId: string
  onClose: () => void
  onArchive: () => void
  onRestore: () => void
}) {
  const update = useUpdateBranch(orgId)
  const [draft, setDraft] = useState<BranchDraft>(draftFromBranch(branch, DEFAULT_ZONE))
  const [place, setPlace] = useState<LocationDraft>(locationDraftOf(branch))
  const [hours, setHours] = useState<HoursDraft>(hoursDraftOf(branch.openingHours))
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const archived = isArchived(branch)
  const placeProblems = locationProblems(place)
  const placeValid = !placeProblems.lat && !placeProblems.lon
  const hourProblems = hoursProblems(hours)
  const hoursValid = Object.keys(hourProblems).length === 0
  const changes = {
    ...draftToUpdate(draft, branch),
    ...(placeValid ? locationToUpdate(place, branch) : {}),
    ...(hoursValid ? hoursToUpdate(hours, branch) : {}),
  }
  const valid = placeValid && hoursValid
  const unchanged = Object.keys(changes).length === 0 && valid

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!draft.name.trim()) {
      setNameError(t.branches.form.nameRequired)
      return
    }
    if (!valid || unchanged) return
    update.mutate({ branchId: branch.id, input: changes }, { onSuccess: onClose })
  }

  return (
    <SidePanel
      open
      wide
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={branch.name}
      description={archived ? t.branches.archived : branchPlace(branch) || undefined}
      onSubmit={submit}
      footer={
        <>
          {archived ? (
            <Button variant="quiet" className="mr-auto -ml-3.5" onClick={onRestore}>
              {t.branches.restoreAction}
            </Button>
          ) : (
            <Button variant="danger" className="mr-auto -ml-3.5" onClick={onArchive}>
              {t.branches.archiveAction}
            </Button>
          )}
          <Button variant="quiet" onClick={onClose}>
            {t.app.cancel}
          </Button>
          <Button type="submit" variant="primary" busy={update.isPending} disabled={unchanged}>
            {update.isPending ? t.app.saving : t.app.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-7">
        <BranchFields
          draft={draft}
          nameError={nameError}
          onChange={(next) => {
            setDraft(next)
            if (next.name.trim()) setNameError(undefined)
          }}
        />
        {archived ? null : (
          <>
            <BranchLocationFields branch={branch} draft={place} problems={placeProblems} onChange={setPlace} />
            <BranchHoursFields draft={hours} problems={hourProblems} onChange={setHours} />
            <BranchCover orgId={orgId} branch={branch} />
          </>
        )}
        {update.isError ? <InlineBanner kind="error" message={errorText(update.error)} /> : null}
      </div>
    </SidePanel>
  )
}

const names = (members: readonly Member[]): string =>
  members.map((m) => m.displayName || t.app.unnamed).join(', ')

/// The question before an archive, with who it touches: by name when the
/// caller may see staff, by number otherwise.
function ArchiveDialog({
  branch,
  orgId,
  members,
  branches,
  onClose,
  onDone,
}: {
  branch: Branch | null
  orgId: string
  members: readonly Member[] | null
  branches: readonly Branch[]
  onClose: () => void
  onDone: (branch: Branch) => void
}) {
  const update = useUpdateBranch(orgId)
  const affected = branch && members ? affectedByArchive(branch.id, members, branches) : null
  const scoped = branch?.scopedMembers ?? 0

  return (
    <ConfirmDialog
      open={Boolean(branch)}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
          update.reset()
        }
      }}
      title={branch ? t.branches.archiveTitle(branch.name) : ''}
      message={
        <span className="flex flex-col gap-2">
          <span>{t.branches.archiveMessage}</span>
          {affected ? (
            <>
              {affected.only.length > 0 ? (
                <span className="text-text">{t.branches.archiveOnly(names(affected.only))}</span>
              ) : null}
              {affected.also.length > 0 ? <span>{t.branches.archiveAlso(names(affected.also))}</span> : null}
            </>
          ) : scoped > 0 ? (
            <span className="text-text">{t.branches.archiveScoped(scoped)}</span>
          ) : null}
        </span>
      }
      confirmLabel={t.branches.archiveConfirm}
      busy={update.isPending}
      error={update.isError ? <InlineBanner kind="error" message={errorText(update.error)} /> : null}
      onConfirm={() => {
        if (!branch) return
        update.mutate(
          { branchId: branch.id, input: { status: 'archived' } },
          { onSuccess: () => onDone(branch) },
        )
      }}
    />
  )
}

function RestoreDialog({
  branch,
  orgId,
  onClose,
  onDone,
}: {
  branch: Branch | null
  orgId: string
  onClose: () => void
  onDone: (branch: Branch) => void
}) {
  const update = useUpdateBranch(orgId)
  return (
    <ConfirmDialog
      open={Boolean(branch)}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
          update.reset()
        }
      }}
      destructive={false}
      title={branch ? t.branches.restoreTitle(branch.name) : ''}
      message={t.branches.restoreMessage}
      confirmLabel={t.branches.restoreConfirm}
      busy={update.isPending}
      error={update.isError ? <InlineBanner kind="error" message={errorText(update.error)} /> : null}
      onConfirm={() => {
        if (!branch) return
        update.mutate(
          { branchId: branch.id, input: { status: 'active' } },
          { onSuccess: () => onDone(branch) },
        )
      }}
    />
  )
}
